import React, { useEffect, useState, useCallback } from 'react'
import { ThemeContext, type ResolvedTheme, type ThemePreference } from './themeContext'

const STORAGE_KEY = 'theme-preference'

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }
  // Migration: check old 'theme' key from previous implementation
  const oldTheme = localStorage.getItem('theme')
  if (oldTheme === 'light' || oldTheme === 'dark') {
    // Migrate to new key
    localStorage.setItem(STORAGE_KEY, oldTheme)
    localStorage.removeItem('theme')
    return oldTheme
  }
  return 'system'
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.remove('light', 'dark')
  root.classList.add(resolved)
  root.style.colorScheme = resolved
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? getSystemTheme() : preference
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>(() => {
    const pref = getStoredPreference()
    // Apply theme immediately on initialization to avoid flash
    const resolved = resolveTheme(pref)
    applyTheme(resolved)
    return pref
  })

  // Derive resolved theme from preference (no separate state needed)
  const resolvedTheme = resolveTheme(preference)

  // Handle system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      if (preference === 'system') {
        applyTheme(getSystemTheme())
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [preference])

  // Apply theme when preference changes and persist to localStorage
  useEffect(() => {
    applyTheme(resolvedTheme)
    localStorage.setItem(STORAGE_KEY, preference)
  }, [preference, resolvedTheme])

  const setTheme = useCallback((theme: ThemePreference) => {
    setPreference(theme)
  }, [])

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
